import {registerCountdown, resolveCountdown} from './raith-countdown.js';
import {readCountdownContext} from './raith-countdown-context.js';

const owners = new WeakMap();
const ownedTimers = new WeakSet();
function registerProductTimer(scope) {
  registerCountdown(scope);
  if (scope.customElements.get('raith-product-countdown')) return;
  const Countdown = scope.customElements.get('raith-countdown');
  scope.customElements.define('raith-product-countdown', class extends Countdown {
    connectedCallback() { if (ownedTimers.has(this)) super.connectedCallback(); else this.hidden = true; }
    refresh() { if (ownedTimers.has(this)) super.refresh(); else this.hidden = true; }
  });
}
const HOST = '[data-raith-product-countdown]';
const STAMP = '[data-raith-cart-content-config]';
const DELIVERY = 'details[data-raith-delivery-disclosure]';
const one = (root, selector) => { const nodes = root.querySelectorAll(selector); return nodes.length === 1 ? nodes[0] : null; };
const same = (a, b) => a && b && ['stamp','identity','section','href','host','product','form','input','variant','productId','ids'].every(key => a[key] === b[key]);

// A private integration dependency, not a public eligibility switch. Automatic boot
// supplies none for delivery. Order cut-off uses the warehouse schedule independently.
export function mountProductCountdown(scope, {assessEligibility} = {}) {
  const document = scope?.document;
  if (!document?.body || !scope.MutationObserver || !scope.crypto?.randomUUID) return null;
  if (owners.has(document)) return owners.get(document);
  const assessor = typeof assessEligibility === 'function' ? assessEligibility : null;
  const instance = scope.crypto.randomUUID(), blocked = new WeakSet(), listeners = [];
  let binding = null, generation = 0, disposed = false, suspended = false, status = 'unknown';
  let timer = null, script = null, timerHost = null, serialized = '', timerConfig = null;
  let line = null, delivery = null;
  const observer = new scope.MutationObserver(records => changed(records));
  function deliveryTarget() {
    if (!binding) return null;
    const target = one(binding.product, DELIVERY);
    if (!target || target.hidden || target.closest('raith-product') !== binding.product || target.dataset.productId !== binding.productId ||
        !/^[\w-]+$/.test(target.id) || one(document, `[id="${target.id}"]`) !== target ||
        target.firstElementChild?.tagName !== 'SUMMARY' || target.firstElementChild.hidden || !target.firstElementChild.textContent.trim()) return null;
    return target;
  }
  function detachInteraction() {
    line?.removeEventListener('click', toggleDelivery);
    delivery?.removeEventListener('toggle', syncInteraction);
    delivery = null;
  }
  function syncInteraction() {
    const target = binding?.config.mode !== 'order_cutoff' && !disposed && !suspended && timer && !timer.hidden && !timerHost.hidden ? deliveryTarget() : null;
    detachInteraction();
    if (!line) return;
    const tag = target ? 'BUTTON' : 'SPAN';
    if (line.tagName !== tag) {
      const replacement = document.createElement(tag.toLowerCase());
      replacement.className = line.className; replacement.setAttribute('aria-live','off');
      while (line.firstChild) replacement.append(line.firstChild);
      line.replaceWith(replacement); line = replacement;
    }
    if (target) {
      delivery = target; if (line.type !== 'button') line.type = 'button';
      line.setAttribute('aria-controls', target.id); line.setAttribute('aria-expanded', String(target.open));
      line.addEventListener('click', toggleDelivery); target.addEventListener('toggle', syncInteraction);
    } else { line.removeAttribute('aria-controls'); line.removeAttribute('aria-expanded'); }
  }
  function toggleDelivery(event) {
    event.preventDefault();
    drain(); syncInteraction();
    if (!delivery || event.currentTarget !== line || timer.hidden || timerHost.hidden) return;
    delivery.open = !delivery.open; syncInteraction();
  }

  function revoke() {
    generation++; status = 'unknown';
    if (timer) {
      // Observe a real false refresh before any later assessed true context.
      script.textContent = JSON.stringify({...timerConfig, active:false, eligible:false}); serialized = script.textContent;
      timer.refresh(); timerHost.hidden = true; syncInteraction();
    }
  }
  function drop() { detachInteraction(); line = null; if (timer) { timer.remove(); ownedTimers.delete(timer); timer = script = timerHost = null; serialized = ''; } }
  function read() {
    const market = readCountdownContext(scope), host = one(document, HOST);
    if (!market || blocked.has(market.stamp) || !host?.isConnected || (!timer && host.childNodes.length)) return null;
    const product = host.closest('raith-product');
    if (!product) return null;
    if (market.config.mode === 'order_cutoff') return {...market, host, product, productId:host.dataset.productId};
    if (product.hasAttribute('data-pending') || !product.hasAttribute('data-selected')) return null;
    const formId = host.dataset.formId, productId = host.dataset.productId;
    if (!/^[1-9]\d*$/.test(productId || '') || !/^[\w-]+$/.test(formId || '') || product.dataset.formId !== formId) return null;
    const form = one(document, `form[id="${formId}"]`);
    if (!form || !product.contains(form)) return null;
    const input = one(form, 'input[name="id"]'), variant = input?.value;
    const ids = host.dataset.variantIds, values = JSON.parse(ids);
    if (!input || input.disabled || !/^[1-9]\d*$/.test(variant || '') || !Array.isArray(values) || !values.length ||
        !values.every(id => Number.isSafeInteger(id) && id > 0) || new Set(values).size !== values.length || !values.some(id => String(id) === variant)) return null;
    return {...market, host, product, form, input, variant, productId, ids};
  }
  function observe() {
    observer.disconnect();
    if (disposed || suspended) return;
    observer.observe(document.documentElement, {childList:true, attributes:true, attributeFilter:['lang']});
    observer.observe(document.body, {childList:true});
    for (const node of [one(document, STAMP), one(document, HOST)?.closest('raith-product')]) {
      if (!node) continue;
      observer.observe(node, {childList:true, subtree:true, characterData:true, attributes:true,
        attributeFilter:['id','name','type','value','disabled','data-pending','data-selected','data-form-id','data-product-id','data-variant-ids','data-raith-product-countdown','data-raith-cart-content-config','open','data-raith-delivery-disclosure','hidden']});
      for (let parent = node.parentElement; parent && parent !== document.body && parent !== document.documentElement; parent = parent.parentElement)
        observer.observe(parent, {childList:true, attributes:true, attributeFilter:['id']});
    }
  }
  function refresh() {
    if (disposed || suspended) return false;
    try {
      const next = read();
      if (!same(binding, next)) { revoke(); if (!next || next.host !== timerHost) drop(); binding = next; }
      if (timer && (timer.parentElement !== timerHost || timerHost.children.length !== 1 || timer.querySelector('script[data-countdown-config]') !== script || script.textContent !== serialized)) {
        revoke(); drop();
      }
      if (binding?.config.mode === 'order_cutoff' && status !== 'schedule') {
        observer.disconnect();
        timerConfig = {...binding.config, active:true, eligible:false};
        ensureTimer(binding);
        serialized = JSON.stringify(timerConfig); script.textContent = serialized;
        timer.refresh(); timerHost.hidden = false; status = 'schedule';
      }
      syncInteraction(); return Boolean(binding);
    } catch { revoke(); drop(); binding = null; return false; }
    finally { observe(); }
  }
  const boundary = node => node.nodeType === 1 && (node.matches(`${HOST},${STAMP},raith-product,form,input[name="id"]`) || node.querySelector(`${HOST},${STAMP},raith-product,form,input[name="id"]`));
  function changed(records) {
    if (disposed || suspended) return;
    const relevant = records.some(record => {
      // Clock text writes are presentation, not a new eligibility context.
      if (timer?.contains(record.target)) return record.target === script || script.contains(record.target);
      if (record.target === binding?.host) return true;
      if (record.type === 'attributes') {
        if (record.target.tagName === 'DETAILS' && record.target.closest('raith-product') === binding?.product &&
            !record.target.contains(binding?.host) && !record.target.contains(binding?.form) &&
            ['open','id','hidden','data-product-id','data-raith-delivery-disclosure'].includes(record.attributeName)) return false;
        return true;
      }
      if (record.target === binding?.stamp || binding?.stamp.contains(record.target)) return true;
      return [...record.addedNodes,...record.removedNodes].some(node => node === timer || boundary(node));
    });
    if (relevant) { revoke(); refresh(); } else syncInteraction();
  }
  function drain() { const records = observer.takeRecords(); if (records.length) changed(records); }
  function block() { const stamp = one(document, STAMP); if (stamp && binding?.config.mode !== 'order_cutoff') blocked.add(stamp); revoke(); binding = null; }
  function ensureTimer(current) {
    if (timer) return;
    registerProductTimer(scope); timer = document.createElement('raith-product-countdown'); ownedTimers.add(timer); timer.className = 'raith-product-countdown'; timer.hidden = true;
    script = document.createElement('script'); script.type = 'application/json'; script.setAttribute('data-countdown-config',''); timer.append(script);
    line = document.createElement('span');
    line.className = 'raith-countdown__line'; line.setAttribute('aria-live','off');
    const mark = document.createElement('span'); mark.textContent = '✓'; mark.setAttribute('aria-hidden','true'); line.append(mark);
    for (const part of ['before','time','after']) { const node = document.createElement(part === 'time' ? 'time' : 'span'); node.setAttribute(`data-countdown-${part}`,''); line.append(node); }
    const presentation = document.createElement('div'); presentation.append(line); timer.append(presentation);
    timerHost = current.host; timerHost.append(timer);
  }
  async function assess() {
    if (disposed || suspended) return false;
    drain(); revoke(); if (!refresh() || !assessor) return false;
    const current = binding, revision = generation, contextId = `${instance}:${revision}`;
    const started = scope.Date.now(); status = 'assessing';
    try {
      const result = await assessor(Object.freeze({contextId, context:Object.freeze({...current.context}), productId:current.productId, variantId:current.variant}));
      // Copy fields before checking generation; getters cannot smuggle a late context.
      const answer = result && {version:result.version, contextId:result.contextId, eligible:result.eligible, checkedAt:result.checkedAt};
      drain(); if (disposed || suspended || generation !== revision || !refresh() || generation !== revision || !same(current,binding)) return false;
      const now = scope.Date.now();
      if (!answer || answer.version !== 1 || answer.contextId !== contextId || answer.eligible !== true || !Number.isFinite(answer.checkedAt) ||
          !Number.isFinite(now) || !Number.isFinite(started) || answer.checkedAt < started || answer.checkedAt > now || now-answer.checkedAt > 60000) { revoke(); return false; }
      const config = {...current.config, eligible:true};
      const atCheck = resolveCountdown(config, answer.checkedAt), atNow = resolveCountdown(config, now);
      if (!atCheck || !atNow || atCheck.date !== atNow.date) { revoke(); return false; }
      observer.disconnect(); timerConfig = config; ensureTimer(current);
      serialized = JSON.stringify(config); script.textContent = serialized; timer.refresh(); timerHost.hidden = false; syncInteraction();
      if (disposed || suspended || generation !== revision) { revoke(); return false; }
      status = timer.hidden ? 'unknown' : 'assessed'; return status === 'assessed';
    } catch { if (generation === revision) revoke(); return false; }
    finally { observe(); }
  }
  const listen = (target,name,fn) => { target.addEventListener(name,fn,true); listeners.push([target,name,fn]); };
  for (const name of ['input','change','submit','product:rerender','variant:change']) listen(document,name,event => {
    if (event.target?.closest?.('form[action*="localization"]')) { block(); return; }
    if (event.target === binding?.form || binding?.product.contains(event.target)) { revoke(); refresh(); }
  });
  for (const name of ['shopify:section:load','shopify:section:unload','shopify:section:reorder']) listen(document,name,() => { revoke(); refresh(); });
  for (const name of ['popstate','hashchange']) listen(scope,name,() => { block(); refresh(); });
  if (scope.navigation?.addEventListener) listen(scope.navigation,'navigate',block);
  listen(document,'visibilitychange',() => { revoke(); refresh(); });
  listen(scope,'pagehide',event => { if (!event.persisted) { dispose(); return; } block(); suspended = true; observer.disconnect(); });
  listen(scope,'pageshow',event => { if (suspended && event.persisted) { suspended = false; refresh(); } });
  function dispose() { if (disposed) return; disposed = true; revoke(); drop(); observer.disconnect(); for (const [target,name,fn] of listeners) target.removeEventListener(name,fn,true); }
  const api = Object.freeze({assess,refresh:() => { drain(); return refresh(); },dispose,state:() => disposed ? 'disposed' : suspended ? 'suspended' : status});
  owners.set(document,api); refresh(); return api;
}
if (typeof window !== 'undefined') {
  if (window.document.readyState === 'loading') window.document.addEventListener('DOMContentLoaded',() => mountProductCountdown(window),{once:true});
  else mountProductCountdown(window);
}
